/**
 * Client-safe entity types and predicates.
 *
 * Split out of entity-resolver.ts because that module pulls node:crypto,
 * node-html-parser and the server fetch helper — none of which may reach a
 * client bundle. The dashboard needs the SHAPE of a resolution and the
 * assertability rule; it must never need the resolver itself.
 */

export type EntitySource =
  | 'jsonld'
  | 'og_site_name'
  | 'title'
  | 'model_grounded'
  | 'domain_fallback'
  /** Not produced by the resolver — set when a human confirms or edits. */
  | 'user_confirmed'

export type EntityConfidence = 'high' | 'medium' | 'low'

export interface EntityEvidence {
  source: EntitySource
  value: string
  weight: number
  /** Where exactly this came from, e.g. 'Organization@https://wreb.de'. */
  detail?: string
}

export interface EntityConflict {
  between: [EntitySource, EntitySource]
  values: [string, string]
  note: string
}

/**
 * The resolution as the dashboard carries it: the resolver's output plus the
 * human decision layered on top.
 */
export interface AuditEntity {
  /**
   * Normalized hostname this entity was resolved FOR. The gate compares it
   * against the domain actually being run, so an entity can never be applied
   * to a different site than the one it describes.
   *
   * Without this, a confirmation made for domain A survived a switch to
   * domain B — `onDomainChange` cleared brand/category but not the entity —
   * and `confirmedByUser` then waved the new domain straight past the gate.
   * Binding the identity to its domain makes that category of bug impossible
   * rather than relying on every reset path remembering to clear state.
   */
  domain: string
  brand: string
  legalName: string | null
  source: EntitySource
  confidence: EntityConfidence
  requiresConfirmation: boolean
  evidence: EntityEvidence[]
  conflicts: EntityConflict[]
  resolverVersion: string
  resolvedAt: string
  /**
   * What the resolver produced BEFORE any human edit. Retained even when the
   * user overrides, so a systematically wrong resolver stays visible in the
   * data instead of being erased by the users who worked around it.
   */
  detectedBrand: string
  /**
   * True once a human confirmed or edited the name. Makes the value
   * authoritative: no later automatic resolution may overwrite it, across
   * paid reruns, refreshes, report regeneration, webhooks or history.
   */
  confirmedByUser: boolean
}

export type RunnableAuditEntity = Pick<
  AuditEntity,
  'domain' | 'brand' | 'confidence' | 'requiresConfirmation' | 'confirmedByUser'
>

/**
 * Is this entity trustworthy enough to assert on the customer's own site?
 *
 * Generators (schema markup, llms.txt, action prompts) must call this before
 * writing a name. Publishing a guessed entity name degrades exactly the
 * first-party signals the audit exists to improve, so the bar is high
 * confidence or an explicit human confirmation — nothing else.
 */
export function isAssertableEntity(
  entity: Pick<AuditEntity, 'confidence' | 'confirmedByUser'> | null | undefined
): boolean {
  if (!entity) return false
  return entity.confirmedByUser || entity.confidence === 'high'
}

/**
 * Normalize any URL or hostname to the key used to bind an entity to its site.
 * Accepts what the domain input accepts, so `wreb.de`, `https://wreb.de/x` and
 * `WWW.WREB.DE` all collapse to one key. Returns '' for unusable input, which
 * never matches and therefore fails closed.
 */
export function entityDomainKey(input: string): string {
  const raw = (input || '').trim()
  if (!raw) return ''
  try {
    const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`)
    return url.hostname.replace(/^www\./i, '').toLowerCase()
  } catch {
    return ''
  }
}

/**
 * May this entity proceed into a paid, multi-tool audit without asking?
 *
 * The product guarantee: a wrong entity cannot SILENTLY reach a paid audit or
 * a generated recommendation. It can still proceed — the user just has to say
 * so first.
 *
 * `domain` is REQUIRED: an entity is only ever valid for the site it was
 * resolved for. Passing a domain the entity does not belong to fails closed,
 * so a stale confirmation carried over from a previous domain cannot approve a
 * new one no matter which reset path missed it.
 *
 * Paid execution boundaries should also pass `expectedBrand`. That binds the
 * provenance to the exact name interpolated into provider prompts, preventing a
 * stale or malformed payload from pairing a trusted entity with another brand.
 */
export function canRunWithoutConfirmation(
  entity: RunnableAuditEntity | null | undefined,
  domain: string,
  expectedBrand?: string
): boolean {
  if (!entity) return false
  if (typeof entity.domain !== 'string' || typeof entity.brand !== 'string') {
    return false
  }
  const target = entityDomainKey(domain)
  if (!target || entityDomainKey(entity.domain) !== target) return false
  if (!entity.brand.trim()) return false
  if (
    expectedBrand !== undefined &&
    (typeof expectedBrand !== 'string' ||
      entity.brand.trim() !== expectedBrand.trim())
  ) {
    return false
  }
  return (
    entity.confirmedByUser === true ||
    (entity.confidence === 'high' && entity.requiresConfirmation === false)
  )
}
